/** @flow */

import React, { useEffect, useState } from "react";
import LabelInput from "../labelInput";
import Button from "../Button";
import { fetchVehicleRegData } from "../../services/simpleListItemsService";

const RegistrationLookup = ({
  formMethods,
  registrationValidation,
  registrationField = "pendingVehicleRegis",
  confirmedRegistrationField = "prevVehicleRegis",
}) => {
  const {
    register,
    formState: { errors },
    watch,
    trigger,
    setValue,
    clearErrors,
  } = formMethods;
  const registration = watch(registrationField);
  const [vehicle, setVehicle] = useState(null);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const [confirmedRegistration, setConfirmedRegistration] = useState("");

  useEffect(() => {
    if (
      isConfirmed &&
      String(registration || "").toUpperCase() !== confirmedRegistration
    ) {
      setVehicle(null);
      setIsConfirmed(false);
    }
  }, [confirmedRegistration, isConfirmed, registration]);

  const handleLookup = async () => {
    setLookupError("");
    const isValid = await trigger(registrationField);

    if (!isValid) {
      return;
    }

    setIsLoading(true);
    try {
      const data = await fetchVehicleRegData(registration);

      if (!data?.isVehicleFound || !data?.vehicleFound) {
        setVehicle(null);
        setIsConfirmed(false);
        setLookupError("We could not find a vehicle for this registration.");
        return;
      }

      setVehicle(data.vehicleFound);
      setConfirmedRegistration(String(registration).toUpperCase());
    } catch {
      setVehicle(null);
      setIsConfirmed(false);
      setLookupError("An error occurred while checking this registration.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeRegistration = (message = "") => {
    setVehicle(null);
    setIsConfirmed(false);
    setConfirmedRegistration("");
    setLookupError(message);
    clearErrors(registrationField);
    clearErrors(confirmedRegistrationField);
    setValue(registrationField, "", { shouldDirty: true });
    setValue(confirmedRegistrationField, null, { shouldDirty: true });
  };

  const handleNo = () => {
    handleChangeRegistration("Please try a different registration.");
  };

  return (
    <div className="tw-w-full tw-max-w-full">
      <label
        className="tw-mb-2 tw-block tw-text-base tw-font-bold tw-text-dis-navy"
        htmlFor={registrationField}
      >
        What is your new registration number?
      </label>
      <div className="tw-flex tw-w-full tw-items-start tw-gap-2.5 max-[640px]:tw-flex-wrap">
        <LabelInput
          id={registrationField}
          type="text"
          placeholder="e.g. EA21 WLP"
          register={register(registrationField, registrationValidation)}
          errors={errors[registrationField]}
          watch={registration}
          trigger={trigger}
          autoComplete="off"
          maxLength={8}
          disabled={isLoading}
          readOnly={isConfirmed}
        />
        <Button
          type="button"
          size="card"
          loading={isLoading && !isConfirmed}
          loadingText="Verifying..."
          className="tw-flex-none"
          onClick={
            isConfirmed ? () => handleChangeRegistration() : handleLookup
          }
        >
          {isConfirmed ? "Change registration" : "Verify registration"}
        </Button>
      </div>

      {vehicle && !isConfirmed && (
        <div className="tw-mt-4 tw-w-full tw-rounded-xl tw-border tw-border-solid tw-border-dis-border-strong tw-bg-dis-info-panel tw-p-4 sm:tw-p-5">
          <div className="tw-flex tw-flex-col tw-gap-1">
            <p className="tw-m-0 tw-text-base tw-font-bold tw-text-dis-navy">
              Is this your vehicle?
            </p>
            <p className="tw-m-0 tw-text-base tw-leading-[1.5] tw-text-dis-text-2">
              {vehicle.abiMake} {vehicle.abiModel}
            </p>
          </div>
          <div className="tw-mt-4 tw-flex tw-flex-wrap tw-gap-2.5">
            <Button
              type="button"
              variant="main"
              size="small"
              onClick={() => {
                setLookupError("");
                setValue(confirmedRegistrationField, registration, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                setIsConfirmed(true);
              }}
            >
              Yes
            </Button>
            <Button
              type="button"
              variant="soft"
              size="small"
              onClick={handleNo}
            >
              No
            </Button>
          </div>
        </div>
      )}

      {lookupError && (
        <span className="tw-mt-2 tw-block tw-text-sm tw-text-dis-error">
          {lookupError}
        </span>
      )}
    </div>
  );
};

export default RegistrationLookup;
